// PRD §22 — UTM URL builder. Pure function wrapping `URL` so the Builder
// page and any future "render a correct link from these params" call-site
// produce identical output.
//
// Behaviour:
// - Keeps any existing query string on `base`; our utm_* values OVERRIDE
//   same-named params already there so the Builder can "re-tag" a URL
//   without first stripping it by hand.
// - Drops empty-string params so we don't emit `?utm_content=`.
// - Preserves the URL fragment.
// - Throws on a malformed `base` (via new URL) — callers should validate
//   input before calling, or catch.

import { SOURCE_TO_PLATFORM, type CanonicalPlatform, type UtmRow } from "./types";

export interface BuildParams {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  sourcePlatform?: string;
}

const PARAM_KEYS: Array<[keyof BuildParams, string]> = [
  ["source", "utm_source"],
  ["medium", "utm_medium"],
  ["campaign", "utm_campaign"],
  ["content", "utm_content"],
  ["term", "utm_term"],
  ["sourcePlatform", "utm_source_platform"],
];

export function buildUtmUrl(base: string, params: BuildParams): string {
  const trimmedBase = base.trim();
  if (!trimmedBase) throw new Error("buildUtmUrl: base URL is required");
  // new URL() accepts the URL as-is, including any existing query/fragment.
  const url = new URL(trimmedBase);
  // Output is rendered as an <a href> in the Builder. Only accept web
  // schemes; anything else (javascript:, data:, file:, ...) could
  // execute script on click or exfiltrate data.
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("buildUtmUrl: only http and https URLs are supported");
  }
  for (const [paramKey, utmName] of PARAM_KEYS) {
    const raw = params[paramKey];
    if (raw === undefined) continue;
    const trimmed = String(raw).trim();
    if (trimmed === "") {
      url.searchParams.delete(utmName);
      continue;
    }
    url.searchParams.set(utmName, trimmed);
  }
  return url.toString();
}

/**
 * Normalize an arbitrary source string into one of the canonical platforms
 * via the SOURCE_TO_PLATFORM synonym map. Case-insensitive. Returns null
 * when the input doesn't match a known synonym — callers can treat it as
 * "Other Social" or surface a prompt to the user.
 */
export function canonicalizeSource(raw: string): CanonicalPlatform | null {
  if (!raw) return null;
  const key = raw.trim().toLowerCase();
  if (!key) return null;
  return SOURCE_TO_PLATFORM[key] ?? null;
}

// Re-export the UtmRow type so Builder consumers can import everything
// from build.ts without reaching into types.ts.
export type { UtmRow };
